<template>
  <div class="footer-menu-group">
    <p v-if="menu.name" class="name" @click="opened = !opened">
      {{ menu.name }}
      <BaseIconSvoi class="icon" :class="{ '-open': opened }" icon="arrow/arrow-down-2" size="s" />
    </p>

    <div
      v-if="menu.items.length"
      class="footer-menu-items collapsible"
      :class="{ '-open': opened }"
    >
      <div v-if="menu.isButtonBlock" class="button-wrapper">
        <a v-for="(item, idx) in menu.items" :key="idx" :href="item.url" class="button">
          <img v-if="item.icon" :src="item.icon" :alt="item.name" />
        </a>
      </div>

      <ul v-else class="nav-list">
        <li v-for="(item, idx) in menu.items" :key="idx" class="nav-item">
          <AppLink v-if="item.url" :to="item.url" class="nav-link">{{ item.name }}</AppLink>
          <span v-else class="nav-link inactive">
            <span class="nav-link-text"> {{ item.name }}</span>
            <sup class="inactive-text">Скоро</sup>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { BaseIconSvoi } from '~/components/base-ui/icon'
  import { AppLink } from '~/components/base-ui/link'
  import type { FooterMenuGroup } from '~/shared/interfaces/menu'

  interface Props {
    menu: FooterMenuGroup
  }

  defineProps<Props>()
  const opened = ref(false)
</script>

<style lang="scss" scoped>
  .footer-menu-group {
    > .name {
      @extend %font-12;

      position: relative;
      display: flex;
      justify-content: space-between;
      letter-spacing: 3px;
      text-transform: uppercase;

      @include vp-767 {
        padding: 16px 0;
        letter-spacing: 0;
        text-transform: none;
        font-weight: 500;
        cursor: pointer;

        &::after {
          content: '';
          height: 1px;
          background: #f0f0f0;
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
        }
      }
    }

    > .name > .icon {
      display: none;

      @include vp-767 {
        display: block;
        transition: $trans-default;

        &.-open {
          transform: rotate(180deg);
        }
      }
    }

    > .footer-menu-items {
      padding-bottom: 4px;
      padding-top: 8px;
    }

    > .footer-menu-items.collapsible {
      @include vp-767 {
        padding: 0;
        overflow: hidden;

        &.-open {
          transition: max-height 0.3s cubic-bezier(1, 0, 1, 0);
          max-height: 500px;
        }

        &:not(.-open) {
          transition: max-height 0.3s cubic-bezier(0, 1, 0, 1);
          max-height: 0;
        }
      }
    }

    > .footer-menu-items > .button-wrapper {
      display: flex;
      align-items: center;
      gap: 12px;

      @include vp-767 {
        padding: 20px 16px;
      }

      > .button {
        width: 48px;
        height: 48px;

        @include vp-767 {
          width: 32px;
          height: 32px;
        }
      }
    }

    > .footer-menu-items > .nav-list {
      @include list-reset;

      @include vp-767 {
        padding: 20px 16px;
      }
    }

    > .footer-menu-items > .nav-list > .nav-item {
      padding: 6px 0;
    }

    > .footer-menu-items > .nav-list > .nav-item > .nav-link {
      @extend %font-14;

      transition: $trans-default;

      @include hover-focus {
        cursor: pointer;
        color: $color-kiwi-50;
      }

      > .inactive-text {
        @extend %font-10;

        padding-left: 4px;
        color: $color-kiwi-50;
      }
    }

    > .footer-menu-items > .nav-list > .nav-item > .nav-link.inactive {
      color: $color-potato-50;
      display: flex;

      @include hover-focus {
        cursor: wait;
      }
    }

    > .footer-menu-items > .nav-list > .nav-item > .nav-link.nuxt-link-exact-active {
      color: $color-kiwi-50;
    }
  }
</style>
